import type { ChainType, WalletGroupedAccounts } from '@polaris/core/models'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { userAccountsQuery } from '@/src/queries/keys/user'
import { useAccountsActions } from '@/src/stores/accounts/actions'
import {
  accountsAtom,
  isConnectingWalletAtom,
  isLocalStateModeAtom,
} from '@/src/stores/accounts/atoms'
import {
  dismissedToastsAtom,
  processedGroupingKeysAtom,
  walletConnectionQueueAtom,
} from '@/src/stores/accounts/detection'
import {
  newlyAddedWalletNameAtom,
  pendingAccountsAtom,
} from '@/src/stores/accounts/wallets'
import {
  changingAccountWalletInfoAtom,
  clearChangingAccountWallet,
  setChangingAccountWallet,
} from '@/src/stores/select-wallets'
import { useAmplitudeAnalytics } from '../use-amplitude-analytics'
import type {
  AggregatedWalletOption,
  ConnectingWallet,
} from './use-wallet-options'

export default function useWalletConnection() {
  const { ampli } = useAmplitudeAnalytics()
  const [connectingWallet, setConnectingWallet] = useState<
    ConnectingWallet | undefined
  >(undefined)
  const [changingAccountWalletInfo, setChangingAccountWalletInfo] = useAtom(
    changingAccountWalletInfoAtom,
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [showMoreOptions, setShowMoreOptions] = useState(false)
  const setConnectionQueue = useSetAtom(walletConnectionQueueAtom)
  const setDismissedToasts = useSetAtom(dismissedToastsAtom)
  // Use the name to ensure the pending wallet state is updated on each render
  const [selectedPendingWalletName, setSelectedPendingWalletName] = useState<
    string | null
  >(null)
  const [pendingWalletAddSuccess, setPendingWalletAddSuccess] =
    useState<WalletGroupedAccounts | null>(null)
  const accounts = useAtomValue(accountsAtom)
  const { groupedAccounts: pendingGroupedAccounts } =
    useAtomValue(pendingAccountsAtom)
  const setNewlyAddedWalletName = useSetAtom(newlyAddedWalletNameAtom)
  // Find the selected pending wallet based on the wallet name
  const selectedPendingWallet = pendingGroupedAccounts.find(
    (account) => account.provider_info.name === selectedPendingWalletName,
  )

  // If in change account screen and a pending account arrives, switch to pending account mode
  useEffect(() => {
    if (
      !selectedPendingWalletName &&
      changingAccountWalletInfo &&
      pendingGroupedAccounts.some(
        (account) =>
          account.provider_info.name ===
          changingAccountWalletInfo.walletOption.displayName,
      )
    ) {
      setSelectedPendingWalletName(
        changingAccountWalletInfo.walletOption.displayName,
      )
    }
  }, [
    changingAccountWalletInfo,
    pendingGroupedAccounts,
    selectedPendingWalletName,
  ])

  const [isConnectingWallet, setIsConnectingWallet] = useAtom(
    isConnectingWalletAtom,
  )
  const { addAccounts, addWalletGroupedAccounts } = useAccountsActions()

  const setProcessedGroupingKeys = useSetAtom(processedGroupingKeysAtom)
  const isLocalState = useAtomValue(isLocalStateModeAtom)

  if (changingAccountWalletInfo) {
    if (changingAccountWalletInfo.connectedAccounts.length !== accounts.length)
      setChangingAccountWalletInfo(clearChangingAccountWallet())
    if (
      accounts.some((account) =>
        changingAccountWalletInfo.connectedAccounts.every(
          (connectedAccount) => connectedAccount.address !== account.address,
        ),
      )
    )
      setChangingAccountWalletInfo(clearChangingAccountWallet())
  }

  const onSelectWallet = (
    wallet: AggregatedWalletOption,
    onConnectionComplete?: () => void,
  ) => {
    const pendingWalletAccount = pendingGroupedAccounts.find(
      (account) => account.provider_info.name === wallet.displayName,
    )
    if (pendingWalletAccount) {
      // Dismiss the toast for the pending wallet since the user is viewing it here
      const toastId = `${pendingWalletAccount.provider_info.name}-${pendingWalletAccount.grouping_key}`
      toast.dismiss(toastId)

      setDismissedToasts((prev) => {
        const newSet = new Set(prev)
        newSet.add(pendingWalletAccount.grouping_key)
        return newSet
      })

      return setSelectedPendingWalletName(
        pendingWalletAccount.provider_info.name,
      )
    }

    // Check if this is a changing account scenario
    const isConnectable = 'connectors' in wallet
    const needsToConnectWallet = isConnectable
      ? Object.keys(wallet.disconnectors || {}).length !==
        Object.keys(wallet.connectors).length
      : true
    const isConnected = isConnectable && !needsToConnectWallet

    if (isConnected) {
      // Already fully connected all ecosystems, prompt to change account
      return setChangingAccountWalletCallback(wallet)
    }

    if (wallet.connect) {
      const supportedEcosystems = Object.keys(wallet.connectors).map(
        (k) => Number(k) as ChainType,
      )

      setIsConnectingWallet(true)
      setConnectionQueue((prev) => {
        const newSet = new Set(prev)
        newSet.add(wallet.displayName)
        return newSet
      })
      wallet
        .connect()
        .then(() => {
          ampli?.walletConnectWalletCompleted({
            wallet: wallet.displayName,
            type: supportedEcosystems.join(', '),
          })
          resetState()
          setNewlyAddedWalletName(wallet.displayName)
          onConnectionComplete?.()
        })
        .catch(() => {
          setConnectionQueue((prev) => {
            const newSet = new Set(prev)
            newSet.delete(wallet.displayName)
            return newSet
          })
        })
        .finally(() => {
          setIsConnectingWallet(false)
        })
    } else {
      throw new Error(`Wallet ${wallet.displayName} is not connectable`)
    }
  }

  const addPendingWallet = useCallback(async () => {
    if (!selectedPendingWallet) {
      console.error('no selectedPendingWallet when adding pending wallet')
      return
    }

    await addWalletGroupedAccounts(selectedPendingWallet)

    setProcessedGroupingKeys((prev) => {
      const newSet = new Set(prev)
      newSet.add(selectedPendingWallet.grouping_key)
      return newSet
    })

    toast.dismiss(
      `${selectedPendingWallet.provider_info.name}-${selectedPendingWallet.grouping_key}`,
    )

    setPendingWalletAddSuccess(selectedPendingWallet)

    if (!isLocalState) {
      await getQueryClient().fetchQuery(userAccountsQuery())
    }
  }, [
    selectedPendingWallet,
    isLocalState,
    addWalletGroupedAccounts,
    setProcessedGroupingKeys,
  ])

  const setChangingAccountWalletCallback = useCallback(
    (wallet: AggregatedWalletOption | undefined) => {
      if (wallet) {
        setChangingAccountWalletInfo(setChangingAccountWallet(wallet, accounts))
      } else setChangingAccountWalletInfo(clearChangingAccountWallet())
    },
    [accounts, setChangingAccountWalletInfo],
  )

  const resetState = useCallback(() => {
    setConnectingWallet(undefined)
    setChangingAccountWalletCallback(undefined)
    setSearchQuery('')
    setShowMoreOptions(false)
    setSelectedPendingWalletName(null)
    setPendingWalletAddSuccess(null)
    setIsConnectingWallet(false)
  }, [setChangingAccountWalletCallback, setIsConnectingWallet])

  return {
    connectingWallet: [connectingWallet, setConnectingWallet] as const,
    changingAccountWallet: [
      changingAccountWalletInfo?.walletOption,
      setChangingAccountWalletCallback,
    ] as const,
    searchQuery: [searchQuery, setSearchQuery] as const,
    showMoreOptions: [showMoreOptions, setShowMoreOptions] as const,
    isConnectingWallet: [isConnectingWallet, setIsConnectingWallet] as const,
    selectedPendingWallet: [
      selectedPendingWallet,
      setSelectedPendingWalletName,
    ] as const,
    isAddingPendingWallet: addAccounts.isPending,
    pendingWalletAddSuccess,
    addPendingWallet,
    resetState,
    onSelectWallet,
  }
}
